
import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, MapPin, Building2 } from 'lucide-react';
import { fadeIn, staggerContainer, getReducedMotionVariants } from '../utils/motion';
import { EnhancedCard } from '@/components/ui/enhanced-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { images } from '@/assets/images';
import { Card, CardContent } from '@/components/ui/card';

interface OfferScopePageProps {
  reducedMotion: boolean;
}

const OfferScopePage: React.FC<OfferScopePageProps> = ({ reducedMotion }) => {
  const intelligenceTools = [
    {
      icon: BarChart3,
      name: "Investor Analytics",
      description: "ROI projections, cash flow analysis, and investment performance metrics"
    },
    {
      icon: MapPin,
      name: "Community Intelligence",
      description: "Neighborhood data, market trends, and location insights"
    },
    {
      icon: Building2,
      name: "Property Tax Database",
      description: "Comprehensive tax records and assessment history"
    }
  ];

  const educationalNotes = [
    {
      id: "payment",
      title: "Payment Clarity",
      description: "See exactly how much of your payment goes to principal, interest, taxes, and insurance."
    },
    {
      id: "investment",
      title: "Investment Insights",
      description: "Understand how your home purchase performs as an investment over time."
    },
    {
      id: "market",
      title: "Market Intelligence",
      description: "Access real-time community data and property tax records for informed decisions."
    }
  ];

  return (
    <div className="space-y-10">
      {/* Header */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4"
      >
        <h2 className="text-3xl font-bold">Investment Intelligence Suite</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Evaluate properties as investments with comprehensive analytics, market data, and community insights
        </p>
      </motion.div>

      {/* Intelligence Tools Grid */}
      <motion.div
        variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {intelligenceTools.map((tool, index) => (
          <motion.div
            key={tool.name}
            variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
          >
            <Card className="h-full">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/10">
                  <tool.icon className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-semibold text-lg">{tool.name}</h3>
                <p className="text-sm text-muted-foreground">{tool.description}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Detailed View */}
    <motion.div
      className="grid grid-cols-1 md:grid-cols-2 gap-8"
      variants={getReducedMotionVariants(staggerContainer(0.2, 0.1), reducedMotion)}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Left Column - Screenshot */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('right', 0.1), reducedMotion)}
        className="flex items-center justify-center md:order-1 lg:order-none"
      >
        <EnhancedCard className="w-full overflow-hidden" outerGlow>
          <img
            src="/lovable-uploads/offerscope-estimator-engine.jpeg"
            alt="EquiForge Estimator Engine - Comprehensive mortgage payment calculator with detailed breakdowns"
            className="w-full h-auto object-cover rounded-xl shadow-lg"
            loading="lazy"
          />
          <div className="p-4 bg-white/5 backdrop-blur-sm">
            <h4 className="font-semibold">EquiForge™ Estimator Engine</h4>
            <p className="text-sm text-muted-foreground">
              Precise mortgage calculations with comprehensive payment breakdowns and analysis.
            </p>
          </div>
        </EnhancedCard>
      </motion.div>

      {/* Right Column - Tool Description */}
      <TooltipProvider delayDuration={300}>
        <motion.div
          variants={getReducedMotionVariants(fadeIn('left', 0.3), reducedMotion)}
          className="flex flex-col justify-center md:order-2 lg:order-none"
        >
          <EnhancedCard className="bg-purple-900/80 dark:bg-purple-950/80 text-white p-6 h-full">
            <div className="space-y-6 flex flex-col h-full">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-500 p-3 rounded-lg">
                  <img
                    src={images.offerscopeLogo}
                    alt="EquiForge Estimator Logo"
                    className="h-12"
                  />
                </div>
                <h2 className="text-2xl font-bold">OfferScope Suite</h2>
              </div>

              <div className="flex-grow space-y-4">
                <h3 className="text-xl font-semibold text-purple-300">Beyond Just Payments</h3>
                <p className="text-gray-300">
                  OfferScope helps you evaluate properties as both homes to live in and investments
                  that build wealth over time.
                </p>

                <div className="space-y-4 pt-2">
                  {educationalNotes.map((note) => (
                    <Tooltip key={note.id}>
                      <TooltipTrigger asChild>
                        <motion.div
                          className="p-3 bg-purple-800/50 rounded-lg cursor-help border border-purple-300/20"
                          whileHover={{ scale: 1.03 }}
                          transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        >
                          <h4 className="font-semibold">{note.title}</h4>
                          <p className="text-sm text-purple-200">Click to learn more</p>
                        </motion.div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p>{note.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>

                <div className="mt-4 pt-2 border-t border-purple-500/30">
                  <h4 className="font-semibold text-purple-200">Precise Payment & Investment Clarity</h4>
                  <p className="text-sm text-gray-300 mt-1">
                    Make confident decisions with complete visibility into both the short and long-term 
                    financial aspects of your home purchase.
                  </p>
                </div>
              </div>
            </div>
          </EnhancedCard>
        </motion.div>
      </TooltipProvider>
      </motion.div>
    </div>
  );
};

export default OfferScopePage;
